/**
 * UserManagement Component
 * 
 * Gerenciamento completo de usuários para Super Admins.
 * Permite visualizar, bloquear/desbloquear e gerenciar permissões de usuários.
 */

import { CabecalhoPagina } from "../bento/ui";
import { useState, useEffect } from 'react';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  formatCpf,
  formatCnpj,
  isValidCpf,
  isValidCnpj,
  stripNonDigits,
} from '@/components/atomic-crm/contacts/document-validation';
import { 
  Shield, 
  Lock, 
  Unlock, 
  Search, 
  AlertCircle,
  Mail,
  Calendar,
  UserCog,
  MoreVertical,
  Trash2,
  Loader2,
  Store
} from 'lucide-react';
import { PageSpinner } from '@/components/ui/page-spinner';
import { PaymentScheduleEditor } from './PaymentScheduleEditor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { estadoCredencial, LinhaConexao, LinhasConexoesFixas } from '../conexoes/Conexoes';
import { useGetIdentity } from 'ra-core';
import { papeisQuePossoDar, useLotacao } from '../unidades/dados';
import { LotacaoDialog } from './LotacaoDialog';
import { ConviteGestaoDialog } from './ConviteGestaoDialog';

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  banned_until: string | null;
  deleted_at: string | null;
  is_super_admin: boolean;
  confirmed_at: string | null;
  /** Unidade da pessoa (migration 308). */
  unidade_id?: string | null;
  unidade_nome?: string | null;
  products: Array<{
    role_code: string;
    role_name: string;
  }>;
}

// Mirrors the migration default in db/supabase/migrations/42_...
// 10 monthly base parcels (0.1%) + 2 bonus parcels in months 6 and 12 (0.5%).
// For flex=100% this yields 2% of the production credit distributed over 12 months.
import {
  canBlockUser,
  canResetPassword,
  canDeleteUser,
  isUserBlocked,
  resolveSchedule,
} from "./user-permissions";
import { MoedaTextoInput } from "@/components/ui/moeda-input";

/** Funções que a tela sabe atribuir — as mesmas que a RPC `update_user_role` aceita. */
type RoleCode = 'user' | 'manager' | 'gerente_unidade' | 'admin_organizacao' | 'super_admin';

const ROLE_LABEL: Record<RoleCode, string> = {
  user: 'Usuário',
  manager: 'Gerente',
  gerente_unidade: 'Responsável pela unidade',
  admin_organizacao: 'Administrador',
  super_admin: 'Super Administrador',
};

/** Papéis da rede de escritórios (migration 308), sem super admin. */
const PAPEIS_DE_REDE: RoleCode[] = ['admin_organizacao', 'gerente_unidade', 'manager'];

const DEFAULT_PAYMENT_SCHEDULE: readonly number[] = [0.1, 0.1, 0.1, 0.1, 0.1, 0.5, 0.1, 0.1, 0.1, 0.1, 0.1, 0.5];
// Mirrors the migration default in db/supabase/migrations/81_... — 0,15% no
// 1º e 2º mês após a venda de quem o gerente gerencia, 0% nos demais.
const TEXTO_FLUXO_PADRAO = "Fluxo padrão de Configurações, vigente na data da venda.";
const DEFAULT_GERENTE_SCHEDULE: readonly number[] = [0.15, 0.15, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

const formatBRL = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDateBR = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR');

// Bloco "Fluxo de Repasse personalizado" para um segmento (imóvel ou móvel).
// Checkbox liga/desliga + editor de 12 meses. Reaproveitado nos diálogos de
// convite e de edição, para imóvel e móvel.
const LabeledScheduleEditor = ({
  idPrefix,
  label,
  enabled,
  value,
  onToggle,
  onChange,
  helpText,
  textoDesligado,
}: {
  idPrefix: string;
  label: string;
  enabled: boolean;
  value: number[];
  onToggle: (checked: boolean) => void;
  onChange: (next: number[]) => void;
  helpText?: string;
  /** O que vale com a caixa desmarcada. */
  textoDesligado?: string;
}) => (
  <div>
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id={`${idPrefix}-enabled`}
        checked={enabled}
        onChange={(e) => onToggle(e.target.checked)}
        className="w-4 h-4"
      />
      <Label htmlFor={`${idPrefix}-enabled`} className="mb-0 cursor-pointer">{label}</Label>
    </div>
    {enabled ? (
      <div className="mt-2">
        <PaymentScheduleEditor value={value} onChange={onChange} helpText={helpText} />
      </div>
    ) : textoDesligado ? (
      <p className="mt-1 ml-6 text-xs text-muted-foreground">{textoDesligado}</p>
    ) : null}
  </div>
);

export const UserManagement = () => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isFirstSuperAdmin, setIsFirstSuperAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [canAccessPage, setCanAccessPage] = useState(false);
  // Rede de escritórios (308): administrador e responsável convidam e lotam
  // pessoas; o super admin também lota pela mesma janela.
  const { data: identity } = useGetIdentity();
  const gestao: 'administrador' | 'responsavel' | null = identity?.perfil_leitura ? null : identity?.gestao_unidade ?? null;
  const { data: lotacao } = useLotacao(gestao === 'responsavel');
  const unidadeFixa = gestao === 'responsavel' ? lotacao?.unidade_id ?? null : null;
  const [papeisPermitidos, setPapeisPermitidos] = useState<string[]>([]);
  const [lotacaoDe, setLotacaoDe] = useState<User | null>(null);
  const [conviteGestao, setConviteGestao] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'block' | 'unblock' | 'delete' | 'reset-password' | null;
    userId: string | null;
    userEmail?: string;
  }>({ open: false, action: null, userId: null });
  /**
   * O que o diálogo de "Inativar acesso" coleta.
   *
   * `enviarEmail` começa DESMARCADO de propósito: mandar e-mail é um ato
   * externo e irreversível, e default marcado faz alguém avisar um cliente sem
   * ter decidido avisar. Quem quer avisar marca.
   */
  const [blockForm, setBlockForm] = useState<{ observacao: string; enviarEmail: boolean }>({
    observacao: '',
    enviarEmail: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Estados para modal de convite
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [roles, setRoles] = useState<Array<{ id: string; code: string; name: string }>>([]);
  const [managers, setManagers] = useState<Array<{ id: number; name: string }>>([]);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    cpfCnpj: '',
    roleId: '',
    isSuperAdmin: false,
    managerCrmUserId: '',      // string id (empty = none)
    taxEnabled: false,         // se false, não envia tax override
    taxModelPct: '',           // decimal pct (vazio = null)
    // Fluxos de repasse por segmento. Se desligado, mantém o default do sistema.
    scheduleImovelEnabled: false,
    paymentScheduleImovel: [...DEFAULT_PAYMENT_SCHEDULE],
    scheduleMovelEnabled: false,
    paymentScheduleMovel: [...DEFAULT_PAYMENT_SCHEDULE],
    // Fluxo de repasse de gerência — só se aplica quando a função é "manager".
    scheduleGerenteEnabled: false,
    paymentScheduleGerente: [...DEFAULT_GERENTE_SCHEDULE],
    // CNAE: se não possui ainda, guarda o prazo (dias) pra regularização.
    hasCnae: true,
    cnaeRegularizationDays: '60',
  });

  // Edit management fields (role, manager, tax, payment schedule). Super_admin only.
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    authUserId: string | null;
    email: string;
    crmUserId: number | null;
    isSuperAdmin: boolean;
    roleCode: RoleCode;
    initialRoleCode: RoleCode;
    managerCrmUserId: string;
    taxEnabled: boolean;
    taxModelPct: string;
    scheduleImovelEnabled: boolean;
    paymentScheduleImovel: number[];
    scheduleMovelEnabled: boolean;
    paymentScheduleMovel: number[];
    scheduleGerenteEnabled: boolean;
    paymentScheduleGerente: number[];
    hasCnae: boolean;
    initialHasCnae: boolean;
    cnaeRegularizationDays: string;
    cnaeDaysTouched: boolean;
    cnaeDeadline: string | null;
    // CPF/CNPJ só dígitos. É a chave que liga o vendedor aos relatórios da
    // Servopa (inadimplentes, comissão, repasse) — antes só dava pra definir
    // no convite.
    cpfCnpj: string;
    initialCpfCnpj: string;
    servopaUsername: string;
    servopaPassword: string;
    /** Só o fato de existir senha — o valor nunca sai do banco. */
    senhaJaCadastrada: boolean;
    initialServopaUsername: string;
    initialServopaPassword: string;
    // Login do AFV Canopus (migration 246). Vazio = vende pela conta da North.
    canopusUsername: string;
    canopusPassword: string;
    canopusSenhaJaCadastrada: boolean;
    initialCanopusUsername: string;
    // "Não permitir incentivo": mora em campaigns.rules.excluidos (elegibilidade
    // da campanha), gravado pela edge function campaign-exclusions.
    noIncentivo: boolean;
    initialNoIncentivo: boolean;
    // Salário fixo (user_salarios): histórico de vigências, só super admin
    // enxerga (RLS). Preencher valor+data cria/corrige uma vigência no salvar.
    salarios: Array<{ id: number; valor_mensal: number; vigente_desde: string }>;
    salarioNovoValor: string;
    salarioNovaData: string;
    saving: boolean;
  }>({
    open: false,
    authUserId: null,
    email: '',
    crmUserId: null,
    isSuperAdmin: false,
    roleCode: 'user',
    initialRoleCode: 'user',
    managerCrmUserId: '',
    taxEnabled: false,
    taxModelPct: '',
    scheduleImovelEnabled: false,
    paymentScheduleImovel: [...DEFAULT_PAYMENT_SCHEDULE],
    scheduleMovelEnabled: false,
    paymentScheduleMovel: [...DEFAULT_PAYMENT_SCHEDULE],
    scheduleGerenteEnabled: false,
    paymentScheduleGerente: [...DEFAULT_GERENTE_SCHEDULE],
    hasCnae: true,
    initialHasCnae: true,
    cnaeRegularizationDays: '60',
    cnaeDaysTouched: false,
    cnaeDeadline: null,
    cpfCnpj: '',
    initialCpfCnpj: '',
    servopaUsername: '',
    servopaPassword: '',
    senhaJaCadastrada: false,
    initialServopaUsername: '',
    initialServopaPassword: '',
    canopusUsername: '',
    canopusPassword: '',
    canopusSenhaJaCadastrada: false,
    initialCanopusUsername: '',
    noIncentivo: false,
    initialNoIncentivo: false,
    salarios: [],
    salarioNovoValor: '',
    salarioNovaData: '',
    saving: false,
  });

  const openEditDialog = async (user: User) => {
    setError(null);
    const { data: crmRow } = await supabase
      .from('users')
      .select('id, cpf_cnpj, manager_id, has_cnae, cnae_regularization_deadline, servopa_username, servopa_senha_definida, canopus_username, canopus_senha_definida')
      .eq('user_id', user.id)
      .maybeSingle();
    // A comissão saiu de `users` (migration 178): lá a linha é legível por todo
    // logado e RLS não filtra coluna, então todo vendedor lia a de todos. Aqui
    // quem chama é super admin, então a política devolve a linha normalmente.
    const { data: comissaoRow } = crmRow?.id
      ? await supabase
          .from('users_comissao')
          .select('tax_model_pct, payment_schedule_imovel, payment_schedule_movel, payment_schedule_gerente')
          .eq('user_id', crmRow.id)
          .maybeSingle()
      : { data: null };
    // Exclusão do incentivo: lida de qualquer campanha de incentivo (a lista é
    // a mesma em todas — a edge function grava em todas de uma vez).
    const { data: campanhaIncentivo } = await supabase
      .from('campaigns')
      .select('rules')
      .eq('kind', 'incentivo_vendas')
      .limit(1)
      .maybeSingle();
    const excluidos: number[] = Array.isArray((campanhaIncentivo?.rules as any)?.excluidos)
      ? ((campanhaIncentivo?.rules as any).excluidos as unknown[]).map(Number)
      : [];
    const noIncentivo = crmRow?.id != null && excluidos.includes(crmRow.id);
    // Histórico de salário fixo — mais recente primeiro.
    const { data: salarios } = crmRow?.id != null
      ? await supabase
          .from('user_salarios')
          .select('id, valor_mensal, vigente_desde')
          .eq('user_id', crmRow.id)
          .order('vigente_desde', { ascending: false })
      : { data: [] };
    // Função atual do alvo. `is_super_admin` vem do RPC e vale mais que o
    // products[], que lista a permissão por produto.
    const currentRoleCode: RoleCode = user.is_super_admin
      ? 'super_admin'
      : (PAPEIS_DE_REDE.find((c) => user.products.some((p) => p.role_code === c)) ?? 'user');
    const imovel = resolveSchedule(comissaoRow?.payment_schedule_imovel, DEFAULT_PAYMENT_SCHEDULE);
    const movel = resolveSchedule(comissaoRow?.payment_schedule_movel, DEFAULT_PAYMENT_SCHEDULE);
    const gerente = resolveSchedule(comissaoRow?.payment_schedule_gerente, DEFAULT_GERENTE_SCHEDULE);
    const servopaUsername = (crmRow?.servopa_username ?? '') as string;
    // A senha NÃO chega mais ao navegador (migration 146). O campo nasce
    // sempre vazio e `senhaJaCadastrada` é o que a tela mostra no lugar —
    // era só isso que o valor real servia pra dizer.
    const servopaPassword = '';
    const senhaJaCadastrada = crmRow?.servopa_senha_definida === true;
    setEditDialog({
      open: true,
      authUserId: user.id,
      email: user.email,
      crmUserId: crmRow?.id ?? null,
      isSuperAdmin: !!user.is_super_admin,
      roleCode: currentRoleCode,
      initialRoleCode: currentRoleCode,
      managerCrmUserId: crmRow?.manager_id ? String(crmRow.manager_id) : '',
      taxEnabled: comissaoRow?.tax_model_pct != null,
      taxModelPct: comissaoRow?.tax_model_pct != null ? String(comissaoRow.tax_model_pct) : '',
      // Personalizado = tem fluxo gravado. Vazio segue o fluxo padrão de
      // Configurações, com vigência (migration 257).
      scheduleImovelEnabled: Array.isArray(comissaoRow?.payment_schedule_imovel),
      paymentScheduleImovel: imovel.schedule,
      scheduleMovelEnabled: Array.isArray(comissaoRow?.payment_schedule_movel),
      paymentScheduleMovel: movel.schedule,
      scheduleGerenteEnabled: gerente.differs,
      paymentScheduleGerente: gerente.schedule,
      hasCnae: crmRow?.has_cnae ?? true,
      initialHasCnae: crmRow?.has_cnae ?? true,
      cnaeRegularizationDays: '60',
      cnaeDaysTouched: false,
      cnaeDeadline: (crmRow?.cnae_regularization_deadline as string | null) ?? null,
      cpfCnpj: stripNonDigits(crmRow?.cpf_cnpj ?? ''),
      initialCpfCnpj: stripNonDigits(crmRow?.cpf_cnpj ?? ''),
      servopaUsername,
      servopaPassword,
      initialServopaUsername: servopaUsername,
      initialServopaPassword: servopaPassword,
      senhaJaCadastrada,
      canopusUsername: (crmRow?.canopus_username ?? '') as string,
      canopusPassword: '',
      canopusSenhaJaCadastrada: crmRow?.canopus_senha_definida === true,
      initialCanopusUsername: (crmRow?.canopus_username ?? '') as string,
      noIncentivo,
      initialNoIncentivo: noIncentivo,
      salarios: salarios ?? [],
      salarioNovoValor: '',
      salarioNovaData: new Date().toISOString().slice(0, 10),
      saving: false,
    });
  };

  const removeSalario = async (salarioId: number) => {
    const { error: delErr } = await supabase
      .from('user_salarios')
      .delete()
      .eq('id', salarioId);
    if (delErr) {
      setError(`Não foi possível remover o salário: ${delErr.message}`);
      return;
    }
    setEditDialog((prev) => ({
      ...prev,
      salarios: prev.salarios.filter((s) => s.id !== salarioId),
    }));
  };

  const handleSaveManagement = async () => {
    if (!editDialog.crmUserId) {
      setError('Usuário sem registro no CRM. É necessário um primeiro acesso à plataforma.');
      return;
    }
    // Mesma validação do convite. Validar antes de gravar qualquer coisa, pra
    // um documento errado não deixar o resto salvo pela metade.
    const cpfCnpjChanged = editDialog.cpfCnpj !== editDialog.initialCpfCnpj;
    if (cpfCnpjChanged) {
      const doc = editDialog.cpfCnpj;
      const valido = doc.length === 11 ? isValidCpf(doc) : doc.length === 14 ? isValidCnpj(doc) : false;
      if (!valido) {
        setError(doc.length === 11 ? 'CPF inválido' : doc.length === 14 ? 'CNPJ inválido' : 'CPF ou CNPJ inválido');
        return;
      }
    }
    setEditDialog({ ...editDialog, saving: true });
    try {
      // 1) Role change (when applicable) — runs first so subsequent UI loads
      //    reflect the new role. Inclui promover/demover super admin: a RPC
      //    (migration 156) só deixa o Super Admin #1 mexer nisso, e recusa
      //    tirar o último super admin ou o próprio.
      if (editDialog.roleCode !== editDialog.initialRoleCode) {
        const { error: roleErr } = await supabase.rpc('update_user_role', {
          p_crm_user_id: editDialog.crmUserId,
          p_role_code: editDialog.roleCode,
        });
        if (roleErr) throw new Error(`Não foi possível mudar a função: ${roleErr.message}`);
      }

      // 2) Management fields (manager, tax, payment schedule).
      // - manager_id só faz sentido pra função "user"; super_admin/manager
      //   nunca têm gerente acima — força null nesses casos.
      // - Imposto desligado → null (limpa).
      // - Fluxo de vendedor desligado → p_padrao_* grava vazio: segue o fluxo
      //   padrão de Configurações, que tem vigência. Uma cópia do padrão não
      //   acompanharia a mudança de vigência.
      // - Fluxo de gerente desligado → DEFAULT explícito (volta ao padrão).
      const canHaveManager = editDialog.roleCode === 'user';
      const { error: rpcErr } = await supabase.rpc('update_user_management', {
        p_crm_user_id: editDialog.crmUserId,
        p_manager_id: (canHaveManager && editDialog.managerCrmUserId ? Number(editDialog.managerCrmUserId) : null) as number,
        p_tax_model_pct: (editDialog.taxEnabled && editDialog.taxModelPct ? Number(editDialog.taxModelPct) : null) as number,
        p_payment_schedule_imovel: editDialog.scheduleImovelEnabled ? editDialog.paymentScheduleImovel : null,
        p_payment_schedule_movel: editDialog.scheduleMovelEnabled ? editDialog.paymentScheduleMovel : null,
        p_payment_schedule_gerente: editDialog.scheduleGerenteEnabled ? editDialog.paymentScheduleGerente : [...DEFAULT_GERENTE_SCHEDULE],
        p_padrao_imovel: !editDialog.scheduleImovelEnabled,
        p_padrao_movel: !editDialog.scheduleMovelEnabled,
      });
      if (rpcErr) throw new Error(rpcErr.message);

      // 2a) CPF/CNPJ. A política de UPDATE de `users` só deixa cada um mexer na
      //     própria linha; a RPC (migration 227) é o caminho do super admin.
      if (cpfCnpjChanged) {
        const { error: docErr } = await supabase.rpc('update_user_cpf_cnpj', {
          p_crm_user_id: editDialog.crmUserId,
          p_cpf_cnpj: editDialog.cpfCnpj,
        });
        if (docErr) throw new Error(docErr.message);
      }

      // 2b) Status de CNAE — só chama quando algo relevante mudou. Recalcula o
      // prazo apenas quando o campo virou "não" agora OU o operador mexeu no
      // campo de dias; senão preservaria o prazo já definido antes (evita que
      // salvar outra coisa qualquer empurre o prazo pra frente sem querer).
      const cnaeJustFlaggedPending = !editDialog.hasCnae && editDialog.initialHasCnae;
      const cnaeStatusChanged = editDialog.hasCnae !== editDialog.initialHasCnae;
      const cnaeDaysChanged = !editDialog.hasCnae && editDialog.cnaeDaysTouched;
      if (cnaeStatusChanged || cnaeDaysChanged) {
        const days = (cnaeJustFlaggedPending || cnaeDaysChanged) && editDialog.cnaeRegularizationDays
          ? Number(editDialog.cnaeRegularizationDays)
          : null;
        const { error: cnaeErr } = await supabase.rpc('update_user_cnae_status', {
          p_crm_user_id: editDialog.crmUserId,
          p_has_cnae: editDialog.hasCnae,
          p_regularization_days: days ?? undefined,
        });
        if (cnaeErr) throw new Error(`Não foi possível salvar o status de CNAE: ${cnaeErr.message}`);
      }

      // 3) Credenciais Servopa. Só persiste se o admin tocou em algum dos
      //    campos (evita reescrever em todo save).
      // Campo de senha em branco = NÃO MEXER, e não "apagar".
      //
      // Antes o campo vinha pré-preenchido com a senha atual, então comparar
      // com o inicial bastava. Agora ele nasce vazio em toda edição (a senha
      // não sai do banco) — se vazio contasse como mudança, todo save de outro
      // campo derrubaria a credencial e o robô de lances pararia sem
      // explicação. A RPC ignora senha nula pelo mesmo motivo.
      const servopaChanged =
        editDialog.servopaUsername !== editDialog.initialServopaUsername ||
        editDialog.servopaPassword.trim() !== '';
      if (servopaChanged) {
        const { error: credErr } = await supabase.rpc('set_user_servopa_credentials', {
          p_crm_user_id: editDialog.crmUserId,
          p_servopa_username: (editDialog.servopaUsername.trim() || null) as string,
          p_servopa_password: (editDialog.servopaPassword.trim() || null) as string,
        });
        if (credErr) throw new Error(`Não foi possível salvar as credenciais Servopa: ${credErr.message}`);
      }

      // 3b) Credenciais Canopus (AFV) — mesma regra: senha em branco = não mexer.
      const canopusChanged =
        editDialog.canopusUsername !== editDialog.initialCanopusUsername ||
        editDialog.canopusPassword.trim() !== '';
      if (canopusChanged) {
        const { error: credErr } = await supabase.rpc('set_user_canopus_credentials', {
          p_crm_user_id: editDialog.crmUserId,
          p_canopus_username: (editDialog.canopusUsername.trim() || null) as string,
          p_canopus_password: (editDialog.canopusPassword.trim() || null) as string,
        });
        if (credErr) throw new Error(`Não foi possível salvar as credenciais Canopus: ${credErr.message}`);
      }

      // 4) Participação no incentivo. Vai por edge function porque a policy de
      //    `campaigns` só dá SELECT pra authenticated — a escrita é service role.
      //    Só chama quando o operador mexeu na caixa.
      if (editDialog.noIncentivo !== editDialog.initialNoIncentivo) {
        const { error: incErr } = await supabase.functions.invoke('campaign-exclusions', {
          method: 'POST',
          body: { crm_user_id: editDialog.crmUserId, excluded: editDialog.noIncentivo },
        });
        if (incErr) throw new Error(`Não foi possível salvar a participação no incentivo: ${incErr.message}`);
      }

      // 5) Salário fixo — só grava quando o operador preencheu um valor novo.
      //    Upsert por (user_id, vigente_desde): corrigir a mesma data
      //    sobrescreve; data nova cria vigência nova e preserva o histórico.
      if (editDialog.salarioNovoValor.trim() !== '') {
        const valor = Number(editDialog.salarioNovoValor.replace(',', '.'));
        if (!Number.isFinite(valor) || valor < 0) {
          throw new Error('Salário inválido. Informe um valor maior ou igual a 0.');
        }
        if (!editDialog.salarioNovaData) {
          throw new Error('Informe a data em que o salário passa a valer.');
        }
        const { error: salErr } = await supabase
          .from('user_salarios')
          .upsert(
            {
              user_id: editDialog.crmUserId,
              valor_mensal: valor,
              vigente_desde: editDialog.salarioNovaData,
            },
            { onConflict: 'user_id,vigente_desde' },
          );
        if (salErr) throw new Error(`Não foi possível salvar o salário: ${salErr.message}`);
      }

      setSuccess('Usuário atualizado.');
      setEditDialog({ ...editDialog, open: false, saving: false });
      setTimeout(() => loadUsers(), 500);
    } catch (e: any) {
      setError(e?.message ?? 'Não foi possível salvar o usuário.');
      setEditDialog({ ...editDialog, saving: false });
    }
  };

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (canAccessPage) {
      (async () => {
        await loadCurrentUser();
        await loadUsers();
      })();
      loadProductsAndRoles();
      void papeisQuePossoDar().then(setPapeisPermitidos);
    }
  }, [canAccessPage]);

  const checkAccess = async () => {
    try {
      // Verificar se é manager
      const { data: isManagerCheck, error: managerError } = await supabase
        .rpc('is_manager');
      
      if (!managerError && isManagerCheck) {
        setIsManager(true);
        setCanAccessPage(true);
        return;
      }

      // Verificar se é super admin
      const { data: isSuperAdminCheck, error: adminError } = await supabase
        .rpc('is_super_admin');

      if (!adminError && isSuperAdminCheck) {
        setIsSuperAdmin(true);
        setCanAccessPage(true);
        return;
      }

      setLoading(false);
    } catch (err) {
      console.error('Error checking access:', err);
      setLoading(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        return user.id;
      }
    } catch (err) {
      console.error('Error loading current user:', err);
    }
    return null;
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar todos os usuários com suas permissões
      const { data: usersData, error: usersError } = await supabase
        .rpc('get_all_users_with_permissions');

      if (usersError) {
        console.error('RPC error:', usersError);
        throw new Error(`Função RPC não encontrada. Execute a migration: ${usersError.message}`);
      }

      setUsers((usersData || []) as User[]);
      setFilteredUsers((usersData || []) as User[]);
      
      // Determinar se o usuário atual é o primeiro super admin criado
      const currentUser = currentUserId ?? (await loadCurrentUser());
      if (currentUser && usersData) {
        const superAdmins = (usersData as User[]).filter((u: User) => u.is_super_admin);
        const sortedByCreation = [...superAdmins].sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        
        // O usuário é o primeiro super admin se for o primeiro criado
        setIsFirstSuperAdmin(sortedByCreation.length > 0 && sortedByCreation[0].id === currentUser);
      }
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Não foi possível carregar os usuários.');
    } finally {
      setLoading(false);
    }
  };

  const loadProductsAndRoles = async () => {
    try {
      // Carregar roles
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('id, code, name')
        .order('hierarchy_level');

      setRoles(rolesData || []);

      // Carregar potenciais gerentes (users com permission ativa de role=manager).
      // Duas queries pra evitar joins complexos com syntax-sensitive Supabase.
      const managerRoleRow = (rolesData ?? []).find((r) => r.code === 'manager');
      if (managerRoleRow) {
        const { data: perms } = await supabase
          .from('user_product_permissions')
          .select('user_id')
          .eq('is_active', true)
          .eq('role_id', managerRoleRow.id);
        const managerAuthIds = Array.from(new Set((perms ?? []).map((p: any) => p.user_id).filter(Boolean)));
        if (managerAuthIds.length > 0) {
          const { data: managerUsers } = await supabase
            .from('users')
            .select('id, name')
            .in('user_id', managerAuthIds)
            .order('name');
          setManagers((managerUsers ?? []).filter((m: any) => m.name).map((m: any) => ({ id: m.id, name: m.name })));
        } else {
          setManagers([]);
        }
      }
    } catch (err) {
      console.error('Error loading products/roles:', err);
    }
  };

  const handleInviteUser = async () => {
    try {
      setInviting(true);
      setError(null);

      if (!inviteForm.email) {
        setError('Email é obrigatório');
        setInviting(false);
        return;
      }

      const cpfCnpjDigits = stripNonDigits(inviteForm.cpfCnpj);
      if (!cpfCnpjDigits) {
        setError('CPF ou CNPJ é obrigatório');
        setInviting(false);
        return;
      }
      if (cpfCnpjDigits.length === 11) {
        if (!isValidCpf(cpfCnpjDigits)) {
          setError('CPF inválido');
          setInviting(false);
          return;
        }
      } else if (cpfCnpjDigits.length === 14) {
        if (!isValidCnpj(cpfCnpjDigits)) {
          setError('CNPJ inválido');
          setInviting(false);
          return;
        }
      } else {
        setError('CPF ou CNPJ inválido');
        setInviting(false);
        return;
      }

      if (!inviteForm.isSuperAdmin && !inviteForm.roleId) {
        setError('Função é obrigatória');
        setInviting(false);
        return;
      }

      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setError('Sessão expirada. Faça login novamente.');
        setInviting(false);
        return;
      }

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`;
      console.log('Calling Edge Function:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session.access_token}`,
        },
        body: JSON.stringify({
          email: inviteForm.email,
          name: inviteForm.name,
          cpfCnpj: cpfCnpjDigits,
          roleId: inviteForm.isSuperAdmin ? null : inviteForm.roleId,
          isSuperAdmin: inviteForm.isSuperAdmin,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Não foi possível enviar o convite.';
        try {
          const result = await response.json();
          errorMessage = result.error || errorMessage;
        } catch {
          if (response.status === 404) {
            errorMessage = 'Edge Function não encontrada. Execute: supabase functions deploy invite-user';
          } else if (response.status === 500) {
            errorMessage = 'Erro no servidor. Verifique os logs da Edge Function.';
          } else {
            errorMessage = `Erro ${response.status}: ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Invite result:', result);

      // After invite creates the CRM users row, persist management fields
      // (gerente, imposto, fluxo de repasse) via RPC. Só dispara quando o
      // operador realmente configurou algo (manager OU checkbox marcada);
      // falha aqui é não-fatal (invite já passou; pode editar depois).
      const hasManagementOverrides =
        inviteForm.managerCrmUserId
        || (inviteForm.taxEnabled && inviteForm.taxModelPct)
        || inviteForm.scheduleImovelEnabled
        || inviteForm.scheduleMovelEnabled
        || inviteForm.scheduleGerenteEnabled;
      if (hasManagementOverrides) {
        try {
          const { data: newUserRow } = await supabase
            .from('users')
            .select('id')
            .eq('email', inviteForm.email)
            .maybeSingle();
          if (newUserRow?.id) {
            const { error: rpcErr } = await supabase.rpc('update_user_management', {
              p_crm_user_id: newUserRow.id,
              p_manager_id: (inviteForm.managerCrmUserId ? Number(inviteForm.managerCrmUserId) : null) as number,
              p_tax_model_pct: (inviteForm.taxEnabled && inviteForm.taxModelPct ? Number(inviteForm.taxModelPct) : null) as number,
              p_payment_schedule_imovel: inviteForm.scheduleImovelEnabled ? inviteForm.paymentScheduleImovel : null,
              p_payment_schedule_movel: inviteForm.scheduleMovelEnabled ? inviteForm.paymentScheduleMovel : null,
              p_payment_schedule_gerente: inviteForm.scheduleGerenteEnabled ? inviteForm.paymentScheduleGerente : null,
            });
            if (rpcErr) console.warn('Failed to persist management fields:', rpcErr.message);
          }
        } catch (mgmtErr) {
          console.warn('Failed to persist management fields:', mgmtErr);
        }
      }

      // CNAE: só grava quando o admin marcou "não possui" — "possui" já é o
      // default da coluna, não precisa de round-trip extra.
      if (!inviteForm.hasCnae) {
        try {
          const { data: newUserRow } = await supabase
            .from('users')
            .select('id')
            .eq('email', inviteForm.email)
            .maybeSingle();
          if (newUserRow?.id) {
            const { error: cnaeErr } = await supabase.rpc('update_user_cnae_status', {
              p_crm_user_id: newUserRow.id,
              p_has_cnae: false,
              p_regularization_days: inviteForm.cnaeRegularizationDays ? Number(inviteForm.cnaeRegularizationDays) : 60,
            });
            if (cnaeErr) console.warn('Failed to persist CNAE status:', cnaeErr.message);
          }
        } catch (cnaeErr) {
          console.warn('Failed to persist CNAE status:', cnaeErr);
        }
      }

      setSuccess('Convite enviado.');
      setInviteDialog(false);

      // Reset form and reload users AFTER dialog close animation finishes
      // to avoid Radix portal/Select cleanup conflicts (removeChild error).
      setTimeout(() => {
        setInviteForm({
          email: '', name: '', cpfCnpj: '', roleId: '', isSuperAdmin: false,
          managerCrmUserId: '', taxEnabled: false, taxModelPct: '', scheduleImovelEnabled: false, paymentScheduleImovel: [...DEFAULT_PAYMENT_SCHEDULE], scheduleMovelEnabled: false, paymentScheduleMovel: [...DEFAULT_PAYMENT_SCHEDULE],
          scheduleGerenteEnabled: false, paymentScheduleGerente: [...DEFAULT_GERENTE_SCHEDULE],
          hasCnae: true, cnaeRegularizationDays: '60',
        });
        setError(null);
      }, 250);
      setTimeout(() => loadUsers(), 1000);
    } catch (err: any) {
      console.error('Error inviting user:', err);
      const errorMessage = err.message || 'Não foi possível convidar o usuário.';
      setError(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  // Bloquear / desbloquear via edge function `block-user`. supabase.auth.admin
  // exige service_role, então o browser não pode chamar direto — a edge
  // function tem as mesmas regras de autorização que delete-user.
  const callBlockUser = async (
    userId: string,
    action: 'block' | 'unblock',
    extras?: { motivo?: string; observacao?: string; enviarEmail?: boolean },
  ) => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setError('Sessão expirada. Faça login novamente.');
        return;
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/block-user`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session.access_token}`,
        },
        body: JSON.stringify({ userId, action, ...extras }),
      });
      if (!response.ok) {
        let errorMessage = action === 'block' ? 'Não foi possível bloquear o usuário.' : 'Não foi possível desbloquear o usuário.';
        try {
          const result = await response.json();
          errorMessage = result.error || errorMessage;
        } catch {
          if (response.status === 404) {
            errorMessage = 'Edge Function não encontrada. Execute: supabase functions deploy block-user';
          }
        }
        throw new Error(errorMessage);
      }
      const corpo = await response.json().catch(() => ({}));
      if (action === 'block') {
        setSuccess(
          corpo?.emailEnviado === false
            ? `Acesso restrito. Não foi possível enviar o aviso por e-mail (${corpo?.emailErro ?? 'motivo desconhecido'}). Comunique o usuário por outro canal.`
            : corpo?.emailEnviado === true
              ? 'Acesso restrito e aviso enviado por e-mail.'
              : 'Acesso restrito.',
        );
      } else {
        setSuccess('Acesso reativado.');
      }
      await loadUsers();
      setActionDialog({ open: false, action: null, userId: null });
    } catch (err: any) {
      console.error(`Error ${action} user:`, err);
      setError(err?.message ?? (action === 'block' ? 'Não foi possível bloquear o usuário.' : 'Não foi possível desbloquear o usuário.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlockUser = (userId: string) =>
    callBlockUser(userId, 'block', {
      observacao: blockForm.observacao,
      enviarEmail: blockForm.enviarEmail,
    });
  const handleUnblockUser = (userId: string) => callBlockUser(userId, 'unblock');

  const handleDeleteUser = async (userId: string) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setError('Sessão expirada. Faça login novamente.');
        return;
      }

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session.access_token}`,
        },
        body: JSON.stringify({
          userId,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Não foi possível excluir o usuário.';
        try {
          const result = await response.json();
          errorMessage = result.error || errorMessage;
        } catch {
          if (response.status === 404) {
            errorMessage = 'Edge Function não encontrada. Execute: supabase functions deploy delete-user';
          } else if (response.status === 500) {
            errorMessage = 'Erro no servidor. Verifique os logs da Edge Function.';
          } else {
            errorMessage = `Erro ${response.status}: ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      setSuccess('Usuário excluído.');
      await loadUsers();
      setActionDialog({ open: false, action: null, userId: null });
    } catch (err: any) {
      console.error('Error deleting user:', err);
      const errorMessage = err.message || 'Não foi possível excluir o usuário.';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (userEmail: string) => {
    try {
      setResettingPassword(true);
      setError(null);
      setSuccess(null);

      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setError('Sessão expirada. Faça login novamente.');
        return;
      }

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session.access_token}`,
        },
        body: JSON.stringify({
          email: userEmail,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Não foi possível enviar o e-mail de redefinição de senha.';
        try {
          const result = await response.json();
          console.error('Reset password error response:', result);
          
          if (result.details) {
            console.error('Error details:', result.details);
            errorMessage = `${result.error || errorMessage}. Detalhes: ${JSON.stringify(result.details)}`;
          } else {
            errorMessage = result.error || errorMessage;
          }
          
          // Check if it's a SMTP configuration error
          if (result.error && result.error.includes('SMTP')) {
            errorMessage = 'SMTP não configurado no Supabase. Configure em: Dashboard > Authentication > SMTP Settings';
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          if (response.status === 404) {
            errorMessage = 'Edge Function não encontrada. Execute: supabase functions deploy reset-password';
          } else if (response.status === 500) {
            errorMessage = 'Erro no servidor. Verifique se o SMTP está configurado no Supabase Dashboard (Authentication > SMTP Settings)';
          } else {
            errorMessage = `Erro ${response.status}: ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Reset password result:', result);

      setSuccess('E-mail de redefinição de senha enviado.');
      setActionDialog({ open: false, action: null, userId: null });
    } catch (err: any) {
      console.error('Error resetting password:', err);
      const errorMessage = err.message || 'Não foi possível enviar o e-mail de redefinição de senha.';
      setError(errorMessage);
    } finally {
      setResettingPassword(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Se não tem acesso, mostrar mensagem
  if (!canAccessPage && !loading) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Sem permissão de acesso a esta página.
            Gestão de usuários restrita a gerentes e super administradores.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return <PageSpinner />;
  }

  return (
    <div>
      <CabecalhoPagina
        titulo={
          <span className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-ouro" />
            Gerenciamento de usuários
          </span>
        }
        descricao={
          gestao === 'administrador'
            ? 'Pessoas da organização'
            : gestao === 'responsavel'
              ? 'Pessoas da unidade'
              : isManager ? 'Usuários convidados por você' : 'Todos os usuários do sistema'
        }
        acoes={
          isManager && gestao ? (
            <Button
              onClick={() => {
                setError(null);
                setConviteGestao(true);
              }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Convidar pessoa
            </Button>
          ) : !isManager ? (
            <Button
              onClick={() => {
                setError(null);
                setInviteDialog(true);
              }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Convidar usuário
            </Button>
          ) : undefined
        }
      />
      <div className="mb-6" />

      {/* Mensagens de sucesso/erro */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6 border-green-500 text-green-700">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Busca */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabela de usuários */}
      <div className="cartao overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Produtos/Funções</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead>Criado em</TableHead>
              <TableHead>Último acesso</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  Nenhum usuário encontrado
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => {
                const blocked = isUserBlocked(user);
                const confirmed = !!user.confirmed_at;

                return (
                  <TableRow key={user.id}>
                    <TableCell className="whitespace-normal">
                      <div className="flex flex-wrap items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="break-all font-medium">{user.email}</span>
                        {user.is_super_admin && (
                          <Badge variant="outline" className="text-purple-600">
                            Super Admin
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {blocked ? (
                        <Badge variant="destructive">
                          <Lock className="h-3 w-3 mr-1" />
                          Bloqueado
                        </Badge>
                      ) : !confirmed ? (
                        <Badge variant="outline" className="border-transparent bg-alerta-fundo text-alerta">
                          Pendente
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-transparent bg-ok-fundo text-ok">
                          <Unlock className="h-3 w-3 mr-1" />
                          Ativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <div className="flex flex-wrap gap-1">
                        {user.products.length === 0 ? (
                          <span className="text-sm text-muted-foreground">
                            Sem função
                          </span>
                        ) : (
                          user.products.map((p, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {p.role_name}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-normal text-sm">
                      {user.unidade_nome ?? <span className="text-muted-foreground">Sem unidade</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(user.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(user.last_sign_in_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline">
                            <MoreVertical className="h-4 w-4 mr-1" />
                            Detalhes
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => setSelectedUser(user)}>
                            <UserCog className="h-4 w-4 mr-2" />
                            Ver detalhes
                          </DropdownMenuItem>
                          {!isManager && (
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <UserCog className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                          )}
                          {(isSuperAdmin || gestao) && (
                            <DropdownMenuItem
                              onClick={() => setLotacaoDe(user)}
                              disabled={!isSuperAdmin && user.id === currentUserId}
                            >
                              <Store className="h-4 w-4 mr-2" />
                              Unidade e equipe
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {blocked ? (
                            <DropdownMenuItem
                              onClick={() =>
                                setActionDialog({
                                  open: true,
                                  action: 'unblock',
                                  userId: user.id,
                                })
                              }
                              disabled={!canBlockUser({ isManager, currentUserId, isFirstSuperAdmin }, user)}
                            >
                              <Unlock className="h-4 w-4 mr-2" />
                              Reativar acesso
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() =>
                                {
                                  setBlockForm({ observacao: '', enviarEmail: false });
                                  setActionDialog({ open: true, action: 'block', userId: user.id });
                                }
                              }
                              disabled={!canBlockUser({ isManager, currentUserId, isFirstSuperAdmin }, user)}
                            >
                              <Lock className="h-4 w-4 mr-2" />
                              Inativar acesso
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                action: 'reset-password',
                                userId: user.id,
                                userEmail: user.email,
                              })
                            }
                            disabled={!canResetPassword({ isManager, currentUserId, isFirstSuperAdmin }, user)}
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            Redefinir senha
                          </DropdownMenuItem>
                          {/* Apenas super_admin pode deletar — gerente fica
                              limitado a Reset de Senha e visualização. */}
                          {!isManager && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() =>
                                  setActionDialog({
                                    open: true,
                                    action: 'delete',
                                    userId: user.id,
                                    userEmail: user.email,
                                  })
                                }
                                disabled={!canDeleteUser({ isManager, currentUserId, isFirstSuperAdmin }, user)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir usuário
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {lotacaoDe && (
        <LotacaoDialog
          pessoa={lotacaoDe}
          pessoas={users}
          papeis={papeisPermitidos}
          unidadeFixa={unidadeFixa}
          onClose={() => setLotacaoDe(null)}
          onSaved={() => {
            setSuccess('Unidade e equipe atualizadas.');
            void loadUsers();
          }}
        />
      )}

      {conviteGestao && (
        <ConviteGestaoDialog
          pessoas={users}
          papeis={papeisPermitidos}
          roles={roles}
          unidadeFixa={unidadeFixa}
          onClose={() => setConviteGestao(false)}
          onInvited={(email) => {
            setSuccess(`Convite enviado para ${email}.`);
            void loadUsers();
          }}
        />
      )}

      {/* Dialog de confirmação de ação */}
      <Dialog
        open={actionDialog.open}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, action: null, userId: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'block'
                ? 'Inativar acesso temporariamente'
                : actionDialog.action === 'unblock'
                  ? 'Reativar acesso'
                  : actionDialog.action === 'delete'
                    ? 'Excluir usuário'
                    : 'Redefinir senha'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'block'
                ? 'O usuário perde o acesso ao sistema. Ação reversível; nenhum dado é apagado.'
                : actionDialog.action === 'unblock'
                  ? 'O usuário volta a acessar o sistema normalmente.'
                  : actionDialog.action === 'delete'
                    ? 'O usuário será excluído permanentemente. Esta ação não pode ser desfeita.'
                    : 'Um e-mail de redefinição de senha será enviado ao usuário.'}
            </DialogDescription>
          </DialogHeader>

          {actionDialog.action === 'block' && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="block-obs">Observação (interna, opcional)</Label>
                <Input
                  id="block-obs"
                  value={blockForm.observacao}
                  onChange={(e) => setBlockForm((f) => ({ ...f, observacao: e.target.value }))}
                  placeholder="ex.: 2ª mensalidade em atraso"
                  disabled={actionLoading}
                />
                {/* Interna e por escrito: daqui a dois meses ninguém lembra por
                    que este acesso foi cortado, e é a cobrança que precisa saber. */}
                <p className="text-[11px] text-muted-foreground">
                  Registrada no histórico, sem visibilidade para o usuário.
                </p>
              </div>

              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="block-email"
                  checked={blockForm.enviarEmail}
                  onCheckedChange={(v: boolean | 'indeterminate') =>
                    setBlockForm((f) => ({ ...f, enviarEmail: v === true }))
                  }
                  disabled={actionLoading}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="block-email" className="cursor-pointer">
                    Avisar o usuário por e-mail
                  </Label>
                  {/* O e-mail NÃO diz o motivo: caixa de entrada é compartilhada
                      e encaminhada, e o motivo é conversa de quem cobra. */}
                  <p className="text-[11px] text-muted-foreground">
                    Informa a restrição e o contato para regularização, sem citar motivo ou valores.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setActionDialog({ open: false, action: null, userId: null })
              }
              disabled={resettingPassword || actionLoading}
            >
              Cancelar
            </Button>
            <Button
              // Só o delete é destrutivo. Inativar acesso é reversível, e
              // pintar de vermelho sugeria um estrago que não existe.
              variant={actionDialog.action === 'delete' ? 'destructive' : 'default'}
              onClick={() => {
                if (actionDialog.userId) {
                  if (actionDialog.action === 'block') {
                    handleBlockUser(actionDialog.userId);
                  } else if (actionDialog.action === 'unblock') {
                    handleUnblockUser(actionDialog.userId);
                  } else if (actionDialog.action === 'delete') {
                    handleDeleteUser(actionDialog.userId);
                  } else if (actionDialog.action === 'reset-password') {
                    handleResetPassword(actionDialog.userEmail || '');
                  }
                }
              }}
              disabled={resettingPassword || actionLoading}
            >
              {resettingPassword ? (
                'Enviando...'
              ) : actionLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processando...
                </span>
              ) : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de detalhes do usuário */}
      <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do usuário</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <span className="text-sm text-muted-foreground">Email</span>
                <p className="font-medium">{selectedUser.email}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Status</span>
                <div className="mt-1">
                  {isUserBlocked(selectedUser) ? (
                    <Badge variant="destructive">Bloqueado</Badge>
                  ) : selectedUser.confirmed_at ? (
                    <Badge variant="outline" className="text-green-600">Ativo</Badge>
                  ) : (
                    <Badge variant="outline" className="text-orange-600">Pendente</Badge>
                  )}
                </div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Função</span>
                <div className="mt-2 space-y-2">
                  {selectedUser.products.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sem função atribuída</p>
                  ) : (
                    selectedUser.products.map((p, idx) => (
                      <Badge key={idx} variant="outline">{p.role_name}</Badge>
                    ))
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">Criado em</span>
                  <p className="text-sm">{formatDate(selectedUser.created_at)}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Último acesso</span>
                  <p className="text-sm">{formatDate(selectedUser.last_sign_in_at)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setSelectedUser(null)}
              className="bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Convite */}
      <Dialog open={inviteDialog} onOpenChange={(open) => {
        setInviteDialog(open);
        if (!open) {
          setError(null);
          setInviteForm({
            email: '', name: '', cpfCnpj: '', roleId: '', isSuperAdmin: false,
            managerCrmUserId: '', taxEnabled: false, taxModelPct: '', scheduleImovelEnabled: false, paymentScheduleImovel: [...DEFAULT_PAYMENT_SCHEDULE], scheduleMovelEnabled: false, paymentScheduleMovel: [...DEFAULT_PAYMENT_SCHEDULE],
            scheduleGerenteEnabled: false, paymentScheduleGerente: [...DEFAULT_GERENTE_SCHEDULE],
          hasCnae: true, cnaeRegularizationDays: '60',
          });
        }
      }}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Convidar novo usuário</DialogTitle>
            <DialogDescription>
              Convite por e-mail com a função e as permissões definidas abaixo.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-email">Email *</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="usuario@exemplo.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="invite-name">Nome</Label>
              <Input
                id="invite-name"
                placeholder="Nome do usuário"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="invite-cpf-cnpj">CPF ou CNPJ *</Label>
              <Input
                id="invite-cpf-cnpj"
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                inputMode="numeric"
                maxLength={18}
                value={
                  // Format on the fly: ≤14 digits → CPF mask; >11 → CNPJ.
                  stripNonDigits(inviteForm.cpfCnpj).length > 11
                    ? formatCnpj(inviteForm.cpfCnpj)
                    : formatCpf(inviteForm.cpfCnpj)
                }
                onChange={(e) => setInviteForm({ ...inviteForm, cpfCnpj: stripNonDigits(e.target.value) })}
              />
            </div>
            {isSuperAdmin && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-md border border-blue-200">
                <input
                  type="checkbox"
                  id="super-admin-check"
                  checked={inviteForm.isSuperAdmin}
                  onChange={(e) => setInviteForm({
                    ...inviteForm,
                    isSuperAdmin: e.target.checked,
                    roleId: e.target.checked ? '' : inviteForm.roleId,
                  })}
                  className="w-4 h-4"
                />
                <Label htmlFor="super-admin-check" className="mb-0 cursor-pointer flex-1">
                  Super Administrador
                </Label>
              </div>
            )}
            {!inviteForm.isSuperAdmin && (() => {
              // Mapeia roleId → code pra condicionar UI (Gerente só aparece pra user).
              const selectedRoleCode = roles.find((r) => r.id === inviteForm.roleId)?.code ?? null;
              return (
                <>
                  <div>
                    <Label htmlFor="invite-role">Função *</Label>
                    <Select
                      value={inviteForm.roleId}
                      onValueChange={(value) => setInviteForm({ ...inviteForm, roleId: value })}
                    >
                      <SelectTrigger id="invite-role">
                        <SelectValue placeholder="Selecione uma função" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles
                          .filter((role) => {
                            // Gerentes só podem convidar usuários comuns.
                            if (isManager && role.code !== 'user') return false;
                            return ['super_admin', 'admin_organizacao', 'gerente_unidade', 'manager', 'user'].includes(role.code);
                          })
                          .map((role) => (
                            <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedRoleCode === 'user' && (
                    <div>
                      <Label htmlFor="invite-manager">Gerente</Label>
                      <Select
                        value={inviteForm.managerCrmUserId || 'none'}
                        onValueChange={(value) => setInviteForm({ ...inviteForm, managerCrmUserId: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger id="invite-manager">
                          <SelectValue placeholder="Sem gerente" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem gerente</SelectItem>
                          {managers.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="invite-tax-enabled"
                        checked={inviteForm.taxEnabled}
                        onChange={(e) => setInviteForm({ ...inviteForm, taxEnabled: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="invite-tax-enabled" className="mb-0 cursor-pointer">Imposto</Label>
                    </div>
                    {inviteForm.taxEnabled && (
                      <Input
                        id="invite-tax"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="ex.: 15.00"
                        value={inviteForm.taxModelPct}
                        onChange={(e) => setInviteForm({ ...inviteForm, taxModelPct: e.target.value })}
                        className="mt-2"
                      />
                    )}
                  </div>

                  <div className="space-y-3">
                    <LabeledScheduleEditor
                      idPrefix="invite-schedule-imovel"
                      label="Fluxo de Repasse personalizado — Imóvel"
                      textoDesligado={TEXTO_FLUXO_PADRAO}
                      enabled={inviteForm.scheduleImovelEnabled}
                      value={inviteForm.paymentScheduleImovel}
                      onToggle={(checked) => setInviteForm({ ...inviteForm, scheduleImovelEnabled: checked })}
                      onChange={(next) => setInviteForm({ ...inviteForm, paymentScheduleImovel: next })}
                    />
                    <LabeledScheduleEditor
                      idPrefix="invite-schedule-movel"
                      label="Fluxo de Repasse personalizado — Móvel"
                      textoDesligado={TEXTO_FLUXO_PADRAO}
                      enabled={inviteForm.scheduleMovelEnabled}
                      value={inviteForm.paymentScheduleMovel}
                      onToggle={(checked) => setInviteForm({ ...inviteForm, scheduleMovelEnabled: checked })}
                      onChange={(next) => setInviteForm({ ...inviteForm, paymentScheduleMovel: next })}
                    />
                    {selectedRoleCode === 'manager' && (
                      <LabeledScheduleEditor
                        idPrefix="invite-schedule-gerente"
                        label="Fluxo de Repasse — Gerente (padrão: 0,15% no 1º e 2º mês)"
                        enabled={inviteForm.scheduleGerenteEnabled}
                        value={inviteForm.paymentScheduleGerente}
                        onToggle={(checked) => setInviteForm({ ...inviteForm, scheduleGerenteEnabled: checked })}
                        onChange={(next) => setInviteForm({ ...inviteForm, paymentScheduleGerente: next })}
                        helpText="Porcentagem da produção das vendas da equipe paga ao gerente em cada mês. Padrão: 0,15% no mês 1 e 0,15% no mês 2, 0% nos demais."
                      />
                    )}
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="invite-has-cnae"
                        checked={inviteForm.hasCnae}
                        onChange={(e) => setInviteForm({ ...inviteForm, hasCnae: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="invite-has-cnae" className="mb-0 cursor-pointer">Possui CNAE?</Label>
                    </div>
                    {!inviteForm.hasCnae && (
                      <div className="mt-2">
                        <Label htmlFor="invite-cnae-days">Prazo para regularização (dias)</Label>
                        <Input
                          id="invite-cnae-days"
                          type="number"
                          inputMode="numeric"
                          min="1"
                          step="1"
                          value={inviteForm.cnaeRegularizationDays}
                          onChange={(e) => setInviteForm({ ...inviteForm, cnaeRegularizationDays: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteDialog(false)}
            >
              Cancelar
            </Button>
            <Button onClick={handleInviteUser} disabled={inviting}>
              {inviting ? 'Enviando...' : 'Enviar convite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit management fields (gerente, modelo tributário, fluxo de pagamento). */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar</DialogTitle>
            <DialogDescription>{editDialog.email}</DialogDescription>
          </DialogHeader>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Tabs defaultValue="dados" className="gap-4">
            <TabsList>
              <TabsTrigger value="dados">Dados</TabsTrigger>
              <TabsTrigger value="repasse">Repasse</TabsTrigger>
              <TabsTrigger value="conexoes">Conexões</TabsTrigger>
            </TabsList>
            {/* forceMount + hidden: os campos das abas fechadas continuam montados
                e o Salvar grava tudo de uma vez, como antes das abas. */}
            <TabsContent value="dados" forceMount className="space-y-4 data-[state=inactive]:hidden">
                <div>
                  <Label htmlFor="edit-cpf-cnpj">CPF ou CNPJ</Label>
                  <Input
                    id="edit-cpf-cnpj"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    inputMode="numeric"
                    maxLength={18}
                    value={
                      editDialog.cpfCnpj.length > 11
                        ? formatCnpj(editDialog.cpfCnpj)
                        : formatCpf(editDialog.cpfCnpj)
                    }
                    onChange={(e) => setEditDialog({ ...editDialog, cpfCnpj: stripNonDigits(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Vincula inadimplentes, comissão e repasse da Servopa a este usuário.
                  </p>
                </div>
                {/* Mexer em super admin — promover alguém ou tirar de quem já é —
                    é do Super Admin #1, a mesma regra de bloquear e deletar. Pros
                    outros o seletor some quando o alvo é super admin, em vez de
                    oferecer uma ação que a RPC vai recusar. */}
                {(!editDialog.isSuperAdmin || isFirstSuperAdmin) && (
                  <div>
                    <Label htmlFor="edit-role">Função</Label>
                    <Select
                      value={editDialog.roleCode}
                      onValueChange={(value) => setEditDialog({ ...editDialog, roleCode: value as RoleCode })}
                    >
                      <SelectTrigger id="edit-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Usuário</SelectItem>
                        <SelectItem value="manager">Gerente</SelectItem>
                        <SelectItem value="gerente_unidade">Responsável pela unidade</SelectItem>
                        <SelectItem value="admin_organizacao">Administrador</SelectItem>
                        {isFirstSuperAdmin && (
                          <SelectItem value="super_admin">Super Administrador</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {editDialog.roleCode !== editDialog.initialRoleCode && (
                      <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        Função será alterada de "{ROLE_LABEL[editDialog.initialRoleCode]}" para "{ROLE_LABEL[editDialog.roleCode]}" ao salvar.
                      </div>
                    )}
                    {editDialog.roleCode === 'super_admin' &&
                      editDialog.initialRoleCode !== 'super_admin' && (
                        <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                          Super administrador tem acesso total, inclusive a salários e credenciais.
                        </div>
                      )}
                  </div>
                )}
                {/* Gerente só aparece pra usuário comum — super_admin/manager
                    não têm gerente acima deles. */}
                {editDialog.roleCode === 'user' && (
                  <div>
                    <Label htmlFor="edit-manager">Gerente</Label>
                    <Select
                      value={editDialog.managerCrmUserId || 'none'}
                      onValueChange={(value) => setEditDialog({ ...editDialog, managerCrmUserId: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger id="edit-manager">
                        <SelectValue placeholder="Sem gerente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem gerente</SelectItem>
                        {managers
                          .filter((m) => m.id !== editDialog.crmUserId)
                          .map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-has-cnae"
                      checked={editDialog.hasCnae}
                      onChange={(e) => setEditDialog({ ...editDialog, hasCnae: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit-has-cnae" className="mb-0 cursor-pointer">Possui CNAE?</Label>
                  </div>
                  {!editDialog.hasCnae && (
                    <div className="mt-2 space-y-1">
                      {editDialog.cnaeDeadline && (
                        <p className={`text-xs ${new Date(editDialog.cnaeDeadline) < new Date() ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                          Prazo atual: {formatDate(editDialog.cnaeDeadline)}
                          {new Date(editDialog.cnaeDeadline) < new Date() ? ' (vencido)' : ''}
                        </p>
                      )}
                      <Label htmlFor="edit-cnae-days">
                        {editDialog.initialHasCnae ? 'Prazo para regularização (dias)' : 'Redefinir prazo (dias, a partir de hoje)'}
                      </Label>
                      <Input
                        id="edit-cnae-days"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        step="1"
                        value={editDialog.cnaeRegularizationDays}
                        onChange={(e) => setEditDialog({ ...editDialog, cnaeRegularizationDays: e.target.value, cnaeDaysTouched: true })}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
            </TabsContent>
            <TabsContent value="repasse" forceMount className="space-y-4 data-[state=inactive]:hidden">
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-tax-enabled"
                      checked={editDialog.taxEnabled}
                      onChange={(e) => setEditDialog({ ...editDialog, taxEnabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit-tax-enabled" className="mb-0 cursor-pointer">Imposto</Label>
                  </div>
                  {editDialog.taxEnabled && (
                    <Input
                      id="edit-tax"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="ex.: 15.00"
                      value={editDialog.taxModelPct}
                      onChange={(e) => setEditDialog({ ...editDialog, taxModelPct: e.target.value })}
                      className="mt-2"
                    />
                  )}
                </div>
                <div className="space-y-3">
                  <LabeledScheduleEditor
                    idPrefix="edit-schedule-imovel"
                    label="Fluxo de Repasse personalizado — Imóvel"
                    textoDesligado={TEXTO_FLUXO_PADRAO}
                    enabled={editDialog.scheduleImovelEnabled}
                    value={editDialog.paymentScheduleImovel}
                    onToggle={(checked) => setEditDialog({ ...editDialog, scheduleImovelEnabled: checked })}
                    onChange={(next) => setEditDialog({ ...editDialog, paymentScheduleImovel: next })}
                  />
                  <LabeledScheduleEditor
                    idPrefix="edit-schedule-movel"
                    label="Fluxo de Repasse personalizado — Móvel"
                    textoDesligado={TEXTO_FLUXO_PADRAO}
                    enabled={editDialog.scheduleMovelEnabled}
                    value={editDialog.paymentScheduleMovel}
                    onToggle={(checked) => setEditDialog({ ...editDialog, scheduleMovelEnabled: checked })}
                    onChange={(next) => setEditDialog({ ...editDialog, paymentScheduleMovel: next })}
                  />
                  {editDialog.roleCode === 'manager' && (
                    <LabeledScheduleEditor
                      idPrefix="edit-schedule-gerente"
                      label="Fluxo de Repasse — Gerente (padrão: 0,15% no 1º e 2º mês)"
                      enabled={editDialog.scheduleGerenteEnabled}
                      value={editDialog.paymentScheduleGerente}
                      onToggle={(checked) => setEditDialog({ ...editDialog, scheduleGerenteEnabled: checked })}
                      onChange={(next) => setEditDialog({ ...editDialog, paymentScheduleGerente: next })}
                      helpText="Porcentagem da produção das vendas da equipe paga ao gerente em cada mês. Padrão: 0,15% no mês 1 e 0,15% no mês 2, 0% nos demais."
                    />
                  )}
                </div>
                {/* Salário fixo — histórico de vigências (user_salarios). Cada
                    mudança vira uma linha nova; o passado nunca é sobrescrito
                    (o custo/lucro por gerente na Performance depende disso).
                    SÓ SUPER ADMIN vê e edita (pedido de 2026-08-25): a RLS já
                    bloqueava o dado, mas a seção aparecia vazia pra gerente —
                    salário nem existir na tela dele é o comportamento certo. */}
                {isSuperAdmin && (
                <div>
                  <Label>Salário fixo</Label>
                  {editDialog.salarios.length === 0 ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Sem salário registrado.
                    </p>
                  ) : (
                    <ul className="mt-1 space-y-1">
                      {editDialog.salarios.map((s, idx) => (
                        <li key={s.id} className="flex items-center gap-2 text-sm">
                          <span className={idx === 0 ? undefined : 'text-muted-foreground'}>
                            {s.valor_mensal > 0
                              ? `${formatBRL(s.valor_mensal)} por mês`
                              : 'Sem salário'}
                            {' — desde '}
                            {formatDateBR(s.vigente_desde)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSalario(s.id)}
                            className="text-muted-foreground hover:text-destructive"
                            title="Remover este registro"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2 mt-2">
                    <MoedaTextoInput
                      id="edit-salario-valor"
                      placeholder="R$ 0,00 por mês"
                      value={editDialog.salarioNovoValor}
                      onValueChange={(v) => setEditDialog({ ...editDialog, salarioNovoValor: v })}
                    />
                    <Input
                      id="edit-salario-data"
                      type="date"
                      value={editDialog.salarioNovaData}
                      onChange={(e) => setEditDialog({ ...editDialog, salarioNovaData: e.target.value })}
                      className="w-40 shrink-0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Vigente a partir da data informada; o histórico é preservado. Para encerrar, registre 0.
                  </p>
                </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-no-incentivo"
                      checked={editDialog.noIncentivo}
                      onChange={(e) => setEditDialog({ ...editDialog, noIncentivo: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit-no-incentivo" className="mb-0 cursor-pointer">
                      Não permitir incentivo
                    </Label>
                  </div>
                  {editDialog.noIncentivo && (
                    <p className="text-xs text-muted-foreground mt-1">
                      A produção conta nos rankings; o prêmio de incentivo é zerado em todas as campanhas, passadas e futuras.
                    </p>
                  )}
                </div>
            </TabsContent>
            <TabsContent value="conexoes" forceMount className="data-[state=inactive]:hidden">
              {/* Servopa e Canopus: só super_admin grava (RPC). Senha em branco
                  mantém a atual; login em branco remove o acesso. */}
              <div className="rounded-[10px] border">
                <LinhaConexao
                  codigo="servopa"
                  sigla="SV"
                  nome="Servopa"
                  detalhe="Lances, inadimplentes e comissões. Login em branco remove o acesso."
                  estado={estadoCredencial(editDialog.initialServopaUsername, editDialog.senhaJaCadastrada)}
                >
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input
                      id="edit-servopa-username"
                      type="text"
                      autoComplete="off"
                      placeholder="Login Servopa"
                      value={editDialog.servopaUsername}
                      onChange={(e) => setEditDialog({ ...editDialog, servopaUsername: stripNonDigits(e.target.value) })}
                    />
                    <Input
                      id="edit-servopa-password"
                      type="text"
                      autoComplete="off"
                      placeholder={
                        editDialog.senhaJaCadastrada
                          ? 'Senha cadastrada (preencher apenas para alterar)'
                          : 'Senha Servopa (não cadastrada)'
                      }
                      value={editDialog.servopaPassword}
                      onChange={(e) => setEditDialog({ ...editDialog, servopaPassword: e.target.value })}
                    />
                  </div>
                </LinhaConexao>
                <LinhaConexao
                  codigo="canopus"
                  sigla="CN"
                  nome="Canopus"
                  detalhe="Sem login próprio, a venda Canopus usa a conta da North."
                  estado={estadoCredencial(editDialog.initialCanopusUsername, editDialog.canopusSenhaJaCadastrada)}
                >
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input
                      id="edit-canopus-username"
                      type="text"
                      autoComplete="off"
                      placeholder="Usuário AFV Canopus"
                      value={editDialog.canopusUsername}
                      onChange={(e) => setEditDialog({ ...editDialog, canopusUsername: e.target.value.trim() })}
                    />
                    <Input
                      id="edit-canopus-password"
                      type="text"
                      autoComplete="off"
                      placeholder={
                        editDialog.canopusSenhaJaCadastrada
                          ? 'Senha cadastrada (preencher apenas para alterar)'
                          : 'Senha Canopus (não cadastrada)'
                      }
                      value={editDialog.canopusPassword}
                      onChange={(e) => setEditDialog({ ...editDialog, canopusPassword: e.target.value })}
                    />
                  </div>
                </LinhaConexao>
                <LinhasConexoesFixas crmUserId={editDialog.crmUserId} />
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ ...editDialog, open: false })}>
              Cancelar
            </Button>
            <Button onClick={handleSaveManagement} disabled={editDialog.saving}>
              {editDialog.saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
